/**
 * 主题与显示偏好（PRD §1.5.1、4.18 U59）：
 * - 主题：跟随系统 / 浅色 / 深色，默认跟随系统；
 * - 减少动效：跟随系统 / 开 / 关；
 * - 涨跌色：三种方案。
 * 偏好先在本机立即生效（PRD 4.18：离线时语言、主题、涨跌色、动效在本机立即生效，联网后同步）。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { UpDownScheme } from './tokens.js';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';
export type ReduceMotionPreference = 'system' | 'on' | 'off';

export const THEME_STORAGE_KEY = 'nx.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false;
}

/** 把主题写到 <html>：data-theme 为实际主题，data-theme-preference 为用户偏好。 */
export function applyTheme(preference: ThemePreference, root: HTMLElement = document.documentElement): ResolvedTheme {
  const resolved = resolveTheme(preference, systemPrefersDark());
  root.dataset.theme = resolved;
  root.dataset.themePreference = preference;
  return resolved;
}

export function applyReduceMotion(preference: ReduceMotionPreference, root: HTMLElement = document.documentElement): void {
  if (preference === 'system') delete root.dataset.reduceMotion;
  else root.dataset.reduceMotion = preference;
}

export function applyUpDownScheme(scheme: UpDownScheme, root: HTMLElement = document.documentElement): void {
  root.dataset.updown = scheme;
}

function readStoredPreference(storageKey: string): ThemePreference | null {
  try {
    const value = window.localStorage.getItem(storageKey);
    return isThemePreference(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStoredPreference(storageKey: string, value: ThemePreference): void {
  try {
    window.localStorage.setItem(storageKey, value);
  } catch {
    // 隐私模式等情况下存储不可用：只在本次会话生效
  }
}

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children: ReactNode;
  /** 没有本机记录时的初始偏好，默认跟随系统 */
  defaultPreference?: ThemePreference;
  storageKey?: string;
}

export function ThemeProvider({ children, defaultPreference = 'system', storageKey = THEME_STORAGE_KEY }: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => (typeof window !== 'undefined' ? readStoredPreference(storageKey) : null) ?? defaultPreference,
  );
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(preference, systemPrefersDark()));

  useEffect(() => {
    setResolved(applyTheme(preference));
    if (preference !== 'system' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => setResolved(applyTheme('system'));
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      writeStoredPreference(storageKey, next);
      setPreferenceState(next);
    },
    [storageKey],
  );

  const value = useMemo(() => ({ preference, resolved, setPreference }), [preference, resolved, setPreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>');
  return value;
}
